import React, { useEffect, useState } from "react";
import { Row, Col, Card, ProgressBar } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { getIndicatorStatesCounter } from "../../../../../rest/indicator";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";

import * as XLSX from "xlsx";
import { buildWorkbookStatus } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorStatus";

import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorStatus() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const resp = await getIndicatorStatesCounter();
        const payload = resp?.data || {};
        setData(payload.data || []);
        setLastUpdate(payload.lastRefreshedAt || null);
        setRefreshVersion(payload.refreshVersion || null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const total = data.reduce((sum, item) => sum + (item?.counter || 0), 0);
  const toPct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);
  const sorted = data.slice().sort((a, b) => b.counter - a.counter);

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookStatus();
      XLSX.writeFile(wb, "indicadores_estado.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel Estado] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const handleExportPdfClient = async () => {
    try {
      await exportPdf("indicadores_estado.pdf");
      setShowExport(false);
    } catch (e) {
      console.error("[PDF Estado] falhou:", e);
      alert("Falha ao gerar PDF: " + (e?.message || e));
    }
  };

  const BarRow = ({ labelNode, value, total }) => {
    const pct = toPct(value, total);
    return (
      <div className="mb-2">
        <div className="d-flex justify-content-between">
          <div className="text-truncate">{labelNode}</div>
          <div className="ms-2">
            <strong>{value}</strong>{" "}
            <span className="text-muted">({pct}%)</span>
          </div>
        </div>
        <ProgressBar now={pct} aria-hidden="true" />
      </div>
    );
  };

  return (
    <div
      className="container-fluid"
      /* todo o container fica exportável */ ref={pdfRef}
    >
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.candidatures.byState"
              defaultMessage="Candidaturas por Estado"
            />
          </h4>
          <small className="text-muted d-block">
            Este indicador contabiliza candidaturas.
          </small>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true" // ?? não sai no PDF
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="text-secondary mb-0">
              <FormattedMessage
                id="indicator.status.distribution.estado"
                defaultMessage="Distribuição por Estado"
              />
            </h6>
            <div className="text-muted">
              <FormattedMessage id="indicator.total" defaultMessage="Total" />:{" "}
              <strong>{total}</strong>
            </div>
          </div>

          {sorted.length === 0 && (
            <div className="text-muted">Sem dados</div>
          )}

          {sorted.map((estado, idx) => (
            <BarRow
              key={idx}
              labelNode={
                <FormattedMessage
                  id={`candidatureState.${estado.state}`}
                  defaultMessage={estado.state}
                />
              }
              value={estado.counter}
              total={total}
            />
          ))}
        </Card.Body>
      </Card>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: "indicadores_estado.xlsx",
          },
          {
            onClick: handleExportPdfClient,
            filename: "indicadores_estado.pdf",
          },
        ]}
      />
    </div>
  );
}
